import React from "react";
import styled, { css } from "styled-components";
import { stylesWrapper } from "@theme/commonStyles";
import { breakpoints } from "@theme/breakpoints";
import { Logo } from "@components/atoms/Logo";
import { Icon } from "@iconify/react/offline";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import { ALL_OFFER_ROUTES, ALL_ROUTES, Routes } from "@routes";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { SOCIAL_MEDIAS } from "@constants";
import { useNewsletterContext } from "@components/molecules/Newsletter";
import { graphql, useStaticQuery } from "gatsby";
import { withLineHoverEffect } from "@theme/withLineHoverEffect";

const StyledWrapper = styled.footer<{
  $newsletterHeight: number;
  $withoutNewsletter: boolean;
}>`
  background: ${({ theme }) => theme.backgroundSecondary};

  ${({ $withoutNewsletter }) =>
    $withoutNewsletter
      ? css`
          padding: 120px 0;
          margin-top: 145px;

          ${breakpoints.laptopMax`
            padding: 90px 0;
          `};

          ${breakpoints.tabletMax`
            padding: 60px 0;
            margin-top: 90px;
          `};

          ${breakpoints.phoneMax`
            padding: 50px 0;
            margin-top: 60px;
          `};
        `
      : css`
          padding: ${({ $newsletterHeight }: any) =>
            `${Math.round($newsletterHeight) + 103}px 0 120px`};

          ${breakpoints.laptopMax`
            padding: ${({ $newsletterHeight }: any) =>
              `${Math.round($newsletterHeight) + 90}px`}
              0 90px;
          `};

          ${breakpoints.tabletMax`
            padding: ${({ $newsletterHeight }: any) =>
              `${Math.round($newsletterHeight) + 60}px`}
              0 60px;
          `};

          ${breakpoints.phoneMax`
            padding: 50px 0;
          `};
        `};
`;

const StyledInnerWrapper = styled.div`
  ${stylesWrapper};
  display: flex;
  justify-content: space-between;
  position: relative;
  gap: 40px;
  align-items: flex-end;

  ${breakpoints.tabletMax`
    max-width: 600px;
    flex-direction: column-reverse;
    align-items: center;
  `};

  ${breakpoints.phoneMax`
    padding-bottom: 0;
    flex-direction: column-reverse;
    align-items: center;
  `}
`;

const StyledFirstColumn = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 485px;
  flex: 1;

  ${breakpoints.laptopMax`
    max-width: 420px;
  `}

  ${breakpoints.phoneMax`
    flex-direction: column-reverse;
  `}
`;

const StyledFirstColumnTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${breakpoints.tabletMax`
    align-items: center;
  `}
`;

const StyledLogo = styled(Logo)`
  width: 100px;

  ${breakpoints.phoneMax`
    width: 80px;
  `};
`;

const StyledCopyright = styled.span`
  margin: 35px 0 15px;
  font-weight: 600;

  ${breakpoints.tabletMax`
    text-align: center;
  `}

  ${breakpoints.phoneMax`
    margin: 20px 0 15px;
  `}
`;

const StyledText = styled.p`
  margin: 0;
  line-height: 1.6;

  ${breakpoints.laptopMax`
    font-size: 1.4rem;
  `}

  ${breakpoints.tabletMax`
    text-align: center;  
  `}
`;

const StyledFirstColumnBottom = styled.div`
  display: flex;
  gap: 72px;
  margin-top: 25px;
  align-items: flex-end;

  ${breakpoints.tabletMax`
    flex-direction: column;
    align-items: center;
    gap: 25px;
  `};

  ${breakpoints.phoneMax`
    margin-top: 0;
    margin-bottom: 25px;
  `};
`;

const StyledContactItemSocialMedia = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;

  a {
    color: ${({ theme }) => theme.foreground};
    text-decoration: none;
    font-size: 2.4rem;
    margin-right: 24px;
    transition: color 0.3s;

    :last-of-type {
      margin-right: 0;
    }

    :hover {
      color: ${({ theme }) => theme.primary};
    }
  }

  ${breakpoints.laptopMax`
    a {
      font-size: 2rem;
      margin-right: 20px;
    }
  `}

  ${breakpoints.phoneMax`
    justify-content: center;
    width: 100%;
  `}
`;

const StyledPrivacyPolicyWrapper = styled.div`
  a {
    font-size: 1.8rem;
    color: ${({ theme }) => theme.washStrong};
    font-weight: 600;
    text-decoration: none;

    ${withLineHoverEffect({ width: "100%" })};
  }

  ${breakpoints.laptopMax`
    a {
      font-size: 1.6rem;
    }
  `}
`;

const StyledSecondColumn = styled.div`
  display: flex;
  gap: 40px;

  ${breakpoints.phoneMax`
    align-items: center;
    flex-direction: column;
  `}
`;

const StyledContactItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${breakpoints.phoneMax`
    align-items: center;
  `}
`;

const StyledHeading = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.washRegular};
  text-transform: uppercase;
  margin: 0;

  ${breakpoints.laptopMax`
    font-size: 1.6rem;
  `}
`;

const StyledListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${breakpoints.phoneMax`
    align-items: center;
  `}
`;

const StyledList = styled.ul<{ $second?: boolean }>`
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  ${({ $second }) =>
    $second &&
    css`
      max-height: 395px;
      flex-wrap: wrap;

      ${breakpoints.laptopMax`
        max-height: 330px;
        `}
    `};

  ${breakpoints.phoneMax`
    align-items: center;
    max-height: unset;
  `}
`;

const StyledItem = styled.li`
  font-size: 2.4rem;
  font-weight: 600;

  :last-of-type {
    margin-bottom: 0;
  }

  a {
    color: ${({ theme }) => theme.foreground};
    text-decoration: none;

    ${withLineHoverEffect({ width: "100%" })};
  }

  ${breakpoints.laptopMax`
    font-size: 1.8rem;
  `}
`;

export const Footer = ({
  withoutNewsletter,
}: {
  withoutNewsletter?: boolean;
}) => {
  const { strapiShared } = useStaticQuery(query);
  const { t } = useTranslation(["footer", "navigation", "offer"]);
  const { height } = useNewsletterContext();
  const { language } = useI18next();

  return (
    <StyledWrapper
      $newsletterHeight={height / 2}
      $withoutNewsletter={withoutNewsletter}
    >
      <StyledInnerWrapper>
        <StyledFirstColumn>
          <StyledFirstColumnTop>
            <StyledLogo />
            <StyledCopyright>
              {t("footer:copyright", { year: strapiShared.year })}
            </StyledCopyright>
            <StyledText>{t("footer:text")}</StyledText>
          </StyledFirstColumnTop>

          <StyledFirstColumnBottom>
            <StyledContactItem>
              <StyledHeading>{t("footer:social-media")}</StyledHeading>
              <StyledContactItemSocialMedia>
                {SOCIAL_MEDIAS(language).map(({ name, icon, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={name}
                    key={name}
                  >
                    <Icon icon={icon} />
                  </a>
                ))}
              </StyledContactItemSocialMedia>
            </StyledContactItem>

            <StyledPrivacyPolicyWrapper>
              <Link to={Routes.PrivacyPolicy}>
                {t("footer:privacy-policy")}
              </Link>
            </StyledPrivacyPolicyWrapper>
          </StyledFirstColumnBottom>
        </StyledFirstColumn>
        <StyledSecondColumn>
          <StyledListWrapper>
            <StyledHeading>{t("footer:subpages")}</StyledHeading>
            <StyledList>
              {ALL_ROUTES.map(({ route, key }) => (
                <StyledItem key={key}>
                  <Link to={route}>{t(`navigation:list.${key}`)}</Link>
                </StyledItem>
              ))}
            </StyledList>
          </StyledListWrapper>
          <StyledListWrapper>
            <StyledHeading>{t("footer:offer")}</StyledHeading>
            <StyledList $second>
              {ALL_OFFER_ROUTES.map(({ route, key }) => (
                <StyledItem key={key}>
                  <Link to={route}>{t(`offer:${key}.footer`)}</Link>
                </StyledItem>
              ))}
            </StyledList>
          </StyledListWrapper>
        </StyledSecondColumn>
      </StyledInnerWrapper>
    </StyledWrapper>
  );
};

const query = graphql`
  {
    strapiShared {
      year
    }
  }
`;
